import { useState, useEffect, useMemo } from 'react';
import { collection, query, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';
import { usePermissions } from '../hooks/usePermissions';
import {
  ClipboardList,
  Search,
  User,
  Clock,
  RefreshCw,
  Shield,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

/**
 * Admin Activity — read-only audit trail for super admins.
 *
 * Reads `admin_activity_logs` in reverse-chronological order. Every
 * mutation across the admin panel (block/unblock, cancel, approve,
 * balance adjust, config change, login/logout) writes one row here via
 * `logAdminActivity()`. Nothing on this page mutates state.
 *
 * Filters are client-side (limit 500 recent entries) — the collection
 * is small enough that this is fine for a long time, and it keeps the
 * page reactive to new entries via a snapshot listener.
 */

// Human-readable label + tone for each `action` verb. Anything not in
// this table falls back to the raw snake_case string, so a new caller
// that adds a new action still renders without breaking the page.
const ACTION_META = {
  // Auth
  login: { label: 'Signed in', tone: 'slate' },
  logout: { label: 'Signed out', tone: 'slate' },
  // Blocked numbers
  block_number: { label: 'Blocked number', tone: 'red' },
  unblock_number: { label: 'Unblocked number', tone: 'green' },
  // Orders
  cancel_order: { label: 'Cancelled order', tone: 'red' },
  // Drivers
  approve_driver: { label: 'Approved driver', tone: 'green' },
  reject_driver: { label: 'Rejected driver', tone: 'red' },
  ban_driver: { label: 'Banned driver', tone: 'red' },
  unban_driver: { label: 'Unbanned driver', tone: 'green' },
  delete_driver: { label: 'Deleted driver', tone: 'red' },
  adjust_driver_balance: { label: 'Adjusted driver balance', tone: 'blue' },
  set_driver_max_distance: { label: 'Set driver max distance', tone: 'blue' },
  clear_driver_max_distance: { label: 'Cleared driver max distance', tone: 'amber' },
  request_driver_docs: { label: 'Requested doc re-upload', tone: 'amber' },
  upload_driver_docs: { label: 'Uploaded driver docs', tone: 'blue' },
  // Customers
  ban_customer: { label: 'Banned customer', tone: 'red' },
  unban_customer: { label: 'Unbanned customer', tone: 'green' },
  delete_customer: { label: 'Deleted customer', tone: 'red' },
  // Agents
  create_agent: { label: 'Created agent', tone: 'green' },
  update_agent: { label: 'Updated agent', tone: 'blue' },
  ban_agent: { label: 'Banned agent', tone: 'red' },
  unban_agent: { label: 'Unbanned agent', tone: 'green' },
  delete_agent: { label: 'Deleted agent', tone: 'red' },
  deposit_to_agent: { label: 'Deposited to agent', tone: 'blue' },
  change_agent_password: { label: 'Changed agent password', tone: 'amber' },
  // Admins
  create_admin: { label: 'Created admin', tone: 'green' },
  update_admin: { label: 'Updated admin', tone: 'blue' },
  delete_admin: { label: 'Deleted admin', tone: 'red' },
  // Delete requests
  approve_delete_request: { label: 'Approved delete request', tone: 'red' },
  reject_delete_request: { label: 'Rejected delete request', tone: 'green' },
  // Notifications
  send_notification: { label: 'Sent notification', tone: 'blue' },
  delete_notification: { label: 'Deleted notification', tone: 'red' },
  // Banners
  create_banner: { label: 'Created banner', tone: 'green' },
  update_banner: { label: 'Updated banner', tone: 'blue' },
  delete_banner: { label: 'Deleted banner', tone: 'red' },
  // Broadcasts
  update_broadcast_config: { label: 'Updated broadcast config', tone: 'blue' },
  toggle_broadcasts: { label: 'Toggled broadcasts', tone: 'amber' },
  // Settings / versions
  update_settings: { label: 'Updated settings', tone: 'blue' },
  update_app_version: { label: 'Updated app version', tone: 'blue' },
  // Notes
  add_note: { label: 'Added note', tone: 'green' },
  delete_note: { label: 'Deleted note', tone: 'red' },
};

const TONE_CLASSES = {
  slate: 'bg-slate-100 text-slate-700',
  red: 'bg-red-50 text-red-700 ring-red-200',
  green: 'bg-green-50 text-green-700 ring-green-200',
  blue: 'bg-blue-50 text-blue-700 ring-blue-200',
  amber: 'bg-amber-50 text-amber-700 ring-amber-200',
};

function formatAction(action) {
  return ACTION_META[action] || {
    label: action.replace(/_/g, ' '),
    tone: 'slate',
  };
}

function fmtTime(ts) {
  if (!ts) return '—';
  const d = ts?.toDate?.() || (ts ? new Date(ts) : null);
  if (!d) return '—';
  return d.toLocaleString('en-GB');
}

const ACTOR_TYPE_LABELS = {
  admin_master: 'Master',
  admin: 'Admin',
  operator: 'Operator',
};

export default function AdminActivity() {
  const { hasPermission } = usePermissions();
  const canView = hasPermission('view_admin_activity');

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('all');
  const [actorTypeFilter, setActorTypeFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 20;

  // Reset to page 1 whenever the filtered set changes — otherwise you
  // can land on page 5 of "all logs", flip a filter that leaves only 12
  // rows, and get a confusing blank table.
  useEffect(() => {
    setCurrentPage(1);
  }, [search, actionFilter, actorTypeFilter]);

  useEffect(() => {
    if (!canView) {
      setLoading(false);
      return;
    }
    const q = query(
      collection(db, 'admin_activity_logs'),
      orderBy('createdAt', 'desc'),
      limit(500),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setLogs(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error('admin_activity_logs listener error:', err);
        setLoading(false);
      },
    );
    return () => unsub();
  }, [canView]);

  const uniqueActions = useMemo(() => {
    const s = new Set(logs.map((l) => l.action).filter(Boolean));
    return Array.from(s).sort();
  }, [logs]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return logs.filter((l) => {
      if (actionFilter !== 'all' && l.action !== actionFilter) return false;
      if (actorTypeFilter !== 'all' && l.actorType !== actorTypeFilter) return false;
      if (!q) return true;
      return (
        (l.actorName || '').toLowerCase().includes(q) ||
        (l.actorEmail || '').toLowerCase().includes(q) ||
        (l.targetLabel || '').toLowerCase().includes(q) ||
        (l.targetId || '').toLowerCase().includes(q) ||
        (l.action || '').toLowerCase().includes(q)
      );
    });
  }, [logs, search, actionFilter, actorTypeFilter]);

  // Page slice off the filtered list. Guard against the current page
  // pointing past the end of the (possibly-shrunken) result set, which
  // happens when filters narrow the list while the user is on page N.
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * PAGE_SIZE;
  const pageRows = filtered.slice(startIndex, startIndex + PAGE_SIZE);

  if (!canView) {
    return (
      <div className="p-6">
        <div className="rounded-xl bg-red-50 ring-1 ring-red-200 px-4 py-3 text-red-700 text-sm">
          Access denied. Admin Activity is restricted to master admins.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ClipboardList className="w-7 h-7 text-primary-600" />
            Admin Activity
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Read-only audit trail of admin & operator actions.
          </p>
        </div>
        <div className="text-right">
          <p className="text-xs text-slate-500 uppercase tracking-wide">Entries</p>
          <p className="text-2xl font-bold text-slate-900">{filtered.length}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search actor, target, or action…"
              className="w-full pl-10 pr-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
            />
          </div>
          <select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-lg bg-white text-sm"
          >
            <option value="all">All actions</option>
            {uniqueActions.map((a) => (
              <option key={a} value={a}>
                {formatAction(a).label}
              </option>
            ))}
          </select>
          <select
            value={actorTypeFilter}
            onChange={(e) => setActorTypeFilter(e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-lg bg-white text-sm"
          >
            <option value="all">All actor types</option>
            <option value="admin_master">Master admin</option>
            <option value="admin">Admin</option>
            <option value="operator">Operator</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-6 h-6 animate-spin text-primary-600" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-slate-500 text-sm">
            <Shield className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            No activity matches this filter.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-left">
                <tr>
                  <th className="px-3 py-2 font-semibold">When</th>
                  <th className="px-3 py-2 font-semibold">Actor</th>
                  <th className="px-3 py-2 font-semibold">Action</th>
                  <th className="px-3 py-2 font-semibold">Target</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((l) => {
                  const meta = formatAction(l.action);
                  const toneClass = TONE_CLASSES[meta.tone] || TONE_CLASSES.slate;
                  return (
                    <tr key={l.id} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="px-3 py-2 text-slate-600 whitespace-nowrap">
                        <span className="inline-flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5" />
                          {fmtTime(l.createdAt)}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex flex-col">
                          <span className="font-medium text-slate-900 inline-flex items-center gap-1">
                            <User className="w-3.5 h-3.5 text-slate-400" />
                            {l.actorName || '—'}
                          </span>
                          <span className="text-xs text-slate-500">
                            {ACTOR_TYPE_LABELS[l.actorType] || l.actorType}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ring-1 ${toneClass}`}
                        >
                          {meta.label}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-slate-700">
                        {l.targetLabel ? (
                          <span>{l.targetLabel}</span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {totalPages > 1 && (
              <div className="border-t border-slate-200 px-4 py-3 bg-slate-50 flex items-center justify-between">
                <div className="text-xs text-slate-600">
                  Showing{' '}
                  <span className="font-semibold">{startIndex + 1}</span>–
                  <span className="font-semibold">
                    {Math.min(startIndex + PAGE_SIZE, filtered.length)}
                  </span>{' '}
                  of <span className="font-semibold">{filtered.length}</span>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={safePage === 1}
                    className="p-1.5 rounded-md border border-slate-300 bg-white hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
                    aria-label="Previous page"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="px-2 text-xs text-slate-700">
                    Page <span className="font-semibold">{safePage}</span>{' '}
                    <span className="text-slate-400">/ {totalPages}</span>
                  </span>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={safePage === totalPages}
                    className="p-1.5 rounded-md border border-slate-300 bg-white hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
                    aria-label="Next page"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
